import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '@iconify/react';
import lockOpen from '@iconify/icons-fa-solid/lock-open';
import chartLine from '@iconify/icons-fa-solid/chart-line';
import boltIcon from '@iconify/icons-fa-solid/bolt';
import shieldAlt from '@iconify/icons-fa-solid/shield-alt';
import { storage } from '@/utils';
import { getLockOptions } from '@/services/vault/vaultService';
import CTAButton from '@/components/cta/button';
import styles from './MobileVaultPanel.module.css';

const VAULT_PATH = '/wallet?mode=vault';
const LOGIN_PATH = '/login?modalType=auth-signin';

const FEATURES = [
  { label: 'No lock-up', icon: lockOpen },
  { label: 'Daily compound', icon: chartLine },
  { label: 'Withdraw anytime', icon: boltIcon },
];

const PARTICLE_COUNT = 12;

const MobileVaultProPanel = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hoursLeft, setHoursLeft] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const logged =
      storage.getKey('logged') === true ||
      localStorage.getItem('logged') === 'true';
    setIsLoggedIn(!!logged);
  }, []);

  // Payout countdown — only when logged in
  useEffect(() => {
    if (!isLoggedIn) return;
    const calc = () => {
      const now = new Date();
      const next = new Date(now);
      next.setUTCHours(18, 30, 0, 0);
      if (now.getUTCHours() > 18 || (now.getUTCHours() === 18 && now.getUTCMinutes() >= 30)) {
        next.setUTCDate(next.getUTCDate() + 1);
      }
      const diff = next - now;
      setHoursLeft(Math.max(1, Math.ceil(diff / 3600000)));
      const total = 24 * 3600000;
      setProgress(Math.min(100, Math.max(0, ((total - diff) / total) * 100)));
    };
    calc();
    const iv = setInterval(calc, 60000);
    return () => clearInterval(iv);
  }, [isLoggedIn]);

  // Fetch lock options to show rates
  const { data: lockOptions = [] } = useQuery({
    queryKey: ['vaultLockOptions'],
    queryFn: getLockOptions,
    staleTime: 60000,
    retry: 2,
  });

  const goToVault = useCallback(
    () => navigate(isLoggedIn ? VAULT_PATH : LOGIN_PATH),
    [navigate, isLoggedIn],
  );

  const particles = useMemo(
    () =>
      Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: 1 + Math.random() * 2.5,
        dur: 2 + Math.random() * 4,
        delay: Math.random() * 3,
      })),
    [],
  );

  // Best rate from lock options
  const bestRate = lockOptions.length > 0
    ? Math.max(...lockOptions.map(o => o.rate))
    : 12;

  return (
    <div className={styles.panel} onClick={goToVault} role="button" tabIndex={0}>
      <div className={styles.borderGlow} />
      <div className={styles.bgLayer} />

      {particles.map((p) => (
        <div
          key={p.id}
          className={styles.particle}
          style={{
            left: `${p.x}%`,
            bottom: `${p.y}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animation: `floatUp ${p.dur}s ease-in-out ${p.delay}s infinite`,
          }}
        />
      ))}

      {/* ── Left content ── */}
      <div className={styles.content}>
        <div className={styles.topRow}>
          {/* APY badge */}
          <div className={styles.apyBadge}>
            <div className={styles.apyShimmer} />
            <div className={styles.apyBlock}>
              <span className={styles.apyNum}>{bestRate}%</span>
              <span className={styles.apyLabel}>APY</span>
            </div>
            <div className={styles.apyDivider} />
            <div className={styles.apySide}>
              <span className={styles.apyEarn}>ANNUAL YIELD</span>
              {isLoggedIn ? (
                <div className={styles.apyPayoutRow}>
                  <span className={styles.payoutDot} />
                  <span className={styles.payoutText}>Payout in {hoursLeft}h</span>
                </div>
              ) : (
                <span className={styles.payoutText}>Up to {bestRate}% APY</span>
              )}
            </div>
          </div>

          {/* Vault branding */}
          {/* <div className={styles.vaultBrand}>
            <Icon icon={shieldAlt} width={24} height={24} className={styles.shieldIcon} />
            <div className={styles.vaultTexts}>
              <span className={styles.vaultLabel}>VAULT PRO</span>
              <span className={styles.vaultSub}>PREMIUM STAKING</span>
            </div>
          </div> */}
        </div>

        {/* Progress bar — logged in only */}
        {isLoggedIn && (
          <div className={styles.progressRow}>
            <span className={styles.progressLabel}>Next payout</span>
            <div className={styles.progressTrack}>
              <div className={styles.progressFill} style={{ width: `${progress}%` }}>
                <div className={styles.progressDot} />
              </div>
            </div>
            <span className={styles.progressTime}>{hoursLeft}h</span>
          </div>
        )}

        {/* Lock options */}
        {lockOptions.length > 0 && (
          <div className={styles.lockOptionsRow}>
            {lockOptions.slice(-3).map((opt) => (
              <div key={opt.value} className={styles.lockOptionChip}>
                <span className={styles.lockOptionLabel}>{opt.label}</span>
                <span className={styles.lockOptionRate}>{opt.rate}%</span>
              </div>
            ))}
          </div>
        )}

        {/* Features + CTA */}
        <div className={styles.bottomRow}>
          <div className={styles.features}>
            {FEATURES.map((f) => (
              <div key={f.label} className={styles.featureItem}>
                <Icon icon={f.icon} width={10} height={10} className={styles.featureIcon} />
                <span className={styles.featureText}>{f.label}</span>
              </div>
            ))}
          </div>

       
        </div>
      </div>

      {/* ── Right: Vault shield image ── */}
      <div className={styles.vaultColumn}>
        <img
          src="/assets/images/vault/vault-shield.webp"
          alt="Vault Pro"
          className={styles.shieldImg}
          loading="lazy"
        />
      </div>

      {/* Full-width CTA at bottom */}
      <div  className={styles.startButtonWrap}>
        <CTAButton onClick={goToVault}>
          {isLoggedIn ? 'Open Vault' : 'Start Earning'}
        </CTAButton>
      </div>
    </div>
  );
};

export default MobileVaultProPanel;